/**
 * A character, actually drawn.
 *
 * The menu used to be a list of names with two numbers beside them, which tells
 * somebody which character is which only if they remember what they called
 * them. This is the same body the world draws, standing still and turning
 * slowly, so a character is recognised rather than read.
 *
 * One of these per card and one on the creation screen, each with its own small
 * renderer. They are given up the moment the card goes, because a browser has
 * only so many drawing contexts to hand out.
 */

import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { Avatar, MODEL_SCALE } from '../game/avatars';

/** How fast it turns, in radians a second. Slow enough to read, not to notice. */
const TURN = 0.5;

export function CharacterPreview({
  skin,
  eyes,
  width = 128,
  height = 168,
}: {
  /** Colours as the world stores them, which is a number rather than a name. */
  skin: number;
  eyes: number;
  width?: number;
  height?: number;
}): JSX.Element {
  const holder = useRef<HTMLDivElement | null>(null);
  const avatar = useRef<Avatar | null>(null);

  // The colours change while somebody is choosing them, and repainting a body
  // costs nothing next to building one, so the scene is built once and dressed
  // whenever the choice moves.
  useEffect(() => {
    avatar.current?.wear({ skin, eyes });
  }, [skin, eyes]);

  useEffect(() => {
    const node = holder.current;
    if (!node) return;

    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    } catch {
      // No drawing context to spare. The card keeps its name and its numbers.
      return;
    }

    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    node.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    scene.add(new THREE.AmbientLight(0xffffff, 0.9));

    const sun = new THREE.DirectionalLight(0xffffff, 1.1);
    sun.position.set(2, 4, 3);
    scene.add(sun);

    // Framed on somebody the size they are in the world, head to foot with a
    // little air above them.
    const camera = new THREE.PerspectiveCamera(32, width / height, 0.1, 40);
    camera.position.set(0, 0.9, 3.3);
    camera.lookAt(0, 0.8, 0);

    const body = new Avatar('person', { skin, eyes }, MODEL_SCALE);
    avatar.current = body;
    scene.add(body.group);

    let frame = 0;
    let last = performance.now();

    const draw = (): void => {
      frame = requestAnimationFrame(draw);

      const now = performance.now();
      const delta = Math.min(0.1, (now - last) / 1000);
      last = now;

      body.update(delta);
      body.group.rotation.y += TURN * delta;
      renderer.render(scene, camera);
    };
    draw();

    return () => {
      cancelAnimationFrame(frame);
      avatar.current = null;
      body.dispose();
      renderer.dispose();
      renderer.domElement.remove();
    };
    // Built once: the colours are applied by the effect above rather than by
    // throwing the body away and making another one.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [width, height]);

  return <div className="preview" ref={holder} style={{ width, height }} />;
}
